import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router";

import type { Role } from "@/types/domain";
import { useAuth } from "@/lib/auth/hooks";
import { can, type Permission } from "@/lib/auth/roles";

/**
 * Redirects to /sign-in when unauthenticated, or to / when authenticated but lacking
 * any of the required permissions. The hook only redirects after the initial auth
 * load completes (useAuth().isLoading === false).
 */
export function useRequireRole(opts: {
  allow?: readonly Role[];
  require?: readonly Permission[];
}): { ready: boolean } {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isLoading) return;

    if (!user) {
      navigate(`/sign-in?from=${encodeURIComponent(location.pathname)}`, { replace: true });
      return;
    }

    if (opts.allow && !opts.allow.includes(user.role)) {
      navigate("/", { replace: true });
      return;
    }

    if (opts.require && !opts.require.every((p) => can(user.role, p))) {
      navigate("/", { replace: true });
    }
  }, [isLoading, user, opts, navigate, location.pathname]);

  return { ready: !isLoading && !!user };
}
